"use client";

import { useSyncExternalStore, useCallback } from "react";

export interface BrowserStoreConfig<T> {
  /** LocalStorage key name */
  readonly key: string;
  /** Static, immutable server fallback returned during SSR and hydration checks */
  readonly serverFallback: T;
  /** Dynamic client initializer called when storage is empty on client */
  readonly clientDefault: () => T;
  /** Custom deserializer; defaults to JSON.parse with error tolerance */
  readonly parse?: (raw: string) => T;
  /** Custom serializer; defaults to JSON.stringify */
  readonly serialize?: (val: T) => string;
}

export interface BrowserStore<T> {
  readonly subscribe: (onChange: () => void) => () => void;
  readonly getSnapshot: () => T;
  readonly getServerSnapshot: () => T;
  readonly set: (updater: T | ((prev: T) => T)) => void;
}

/**
 * Tier 5 Structural Browser Store Factory:
 * Enforces referential stability of getServerSnapshot and caches parsed client storage snapshots.
 */
export function createBrowserStore<T>(config: BrowserStoreConfig<T>): BrowserStore<T> {
  const {
    key,
    serverFallback,
    clientDefault,
    parse = (raw: string): T => JSON.parse(raw) as T,
    serialize = (val: T): string => JSON.stringify(val),
  } = config;

  // Guarantee immutable singleton reference for server snapshot
  const frozenServerFallback = Object.freeze(serverFallback);

  let rawCache: string | null = null;
  let parsedCache: T = frozenServerFallback;
  let isInitialized = false;
  let cachedStorageRef: Storage | null = null;

  const listeners = new Set<() => void>();

  const getSnapshot = (): T => {
    if (typeof window === "undefined") {
      return frozenServerFallback;
    }

    try {
      const storage = window.localStorage;
      if (isInitialized && listeners.size > 0 && storage === cachedStorageRef) {
        return parsedCache;
      }
      const raw = storage.getItem(key);
      if (!isInitialized || storage !== cachedStorageRef || raw !== rawCache) {
        cachedStorageRef = storage;
        rawCache = raw;
        if (raw === null) {
          parsedCache = clientDefault();
        } else {
          parsedCache = parse(raw);
        }
        isInitialized = true;
      }
    } catch {
      // Fallback to clientDefault on corrupt JSON or blocked storage
      rawCache = null;
      parsedCache = clientDefault();
      isInitialized = true;
    }

    return parsedCache;
  };

  const getServerSnapshot = (): T => frozenServerFallback;

  const notify = () => {
    for (const listener of listeners) {
      listener();
    }
  };

  const subscribe = (onChange: () => void): (() => void) => {
    listeners.add(onChange);

    const handleStorage = (event: StorageEvent) => {
      // Only notify if event key matches or if storage was cleared (key === null)
      if (event.key === key || event.key === null) {
        if (typeof window !== "undefined") {
          try {
            const currentRaw = window.localStorage.getItem(key);
            if (currentRaw !== rawCache) {
              rawCache = currentRaw;
              if (currentRaw === null) {
                parsedCache = clientDefault();
              } else {
                parsedCache = parse(currentRaw);
              }
              onChange();
            }
          } catch {
            parsedCache = clientDefault();
            onChange();
          }
        }
      }
    };

    if (
      typeof window !== "undefined" &&
      typeof window.addEventListener === "function"
    ) {
      window.addEventListener("storage", handleStorage);
    }

    return () => {
      listeners.delete(onChange);
      if (
        typeof window !== "undefined" &&
        typeof window.removeEventListener === "function"
      ) {
        window.removeEventListener("storage", handleStorage);
      }
    };
  };

  const set = (updater: T | ((prev: T) => T)): void => {
    if (typeof window === "undefined") return;

    const prev = getSnapshot();
    const next = typeof updater === "function" ? (updater as (prev: T) => T)(prev) : updater;

    try {
      const serialized = serialize(next);
      window.localStorage.setItem(key, serialized);
      rawCache = serialized;
      parsedCache = next;
    } catch {
      // In-memory fallback if localStorage fails (e.g. quota exceeded)
      parsedCache = next;
    }

    notify();
    if (typeof window.dispatchEvent === "function") {
      try {
        window.dispatchEvent(new StorageEvent("storage", { key }));
      } catch {
        window.dispatchEvent(new Event("storage"));
      }
    }
  };

  return {
    subscribe,
    getSnapshot,
    getServerSnapshot,
    set,
  };
}

export function useBrowserStore<T>(store: BrowserStore<T>): [T, (updater: T | ((prev: T) => T)) => void] {
  const value = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot);

  const set = useCallback(
    (updater: T | ((prev: T) => T)) => {
      store.set(updater);
    },
    [store]
  );

  return [value, set];
}

const emptySubscribe = () => () => {};

export function useIsMounted(): boolean {
  return useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false
  );
}

/**
 * Shared IndexedDB + In-Memory Map fallback key-value storage engine for client stores.
 */
export class IndexedDbKvEngine<TRecord extends { readonly id: string }> {
  private readonly dbName: string;
  private readonly storeName: string;
  private readonly version: number;
  private readonly warnPrefix: string;
  private readonly memoryStore = new Map<string, TRecord>();
  private dbPromise: Promise<IDBDatabase | null> | null = null;

  constructor(
    dbName: string,
    storeName: string,
    warnPrefix: string = "[IndexedDbKvEngine]",
    version: number = 1
  ) {
    this.dbName = dbName;
    this.storeName = storeName;
    this.warnPrefix = warnPrefix;
    this.version = version;
  }

  private isIndexedDBAvailable(): boolean {
    return typeof indexedDB !== "undefined" && indexedDB !== null;
  }

  private async getDB(): Promise<IDBDatabase | null> {
    if (!this.isIndexedDBAvailable()) {
      return null;
    }
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise<IDBDatabase | null>((resolve) => {
      try {
        const request = indexedDB.open(this.dbName, this.version);

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains(this.storeName)) {
            db.createObjectStore(this.storeName, { keyPath: "id" });
          }
        };

        request.onsuccess = (event) => {
          resolve((event.target as IDBOpenDBRequest).result);
        };

        request.onerror = () => {
          console.warn(`${this.warnPrefix} Failed to open IndexedDB, falling back to memory store.`);
          resolve(null);
        };
      } catch {
        resolve(null);
      }
    });

    return this.dbPromise;
  }

  async put(record: TRecord): Promise<void> {
    const db = await this.getDB();
    if (!db) {
      this.memoryStore.set(record.id, record);
      return;
    }

    return new Promise<void>((resolve, reject) => {
      try {
        const tx = db.transaction(this.storeName, "readwrite");
        const store = tx.objectStore(this.storeName);
        const request = store.put(record);

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      } catch {
        this.memoryStore.set(record.id, record);
        resolve();
      }
    });
  }

  async get(id: string): Promise<TRecord | null> {
    const db = await this.getDB();
    if (!db) {
      return this.memoryStore.get(id) ?? null;
    }

    return new Promise<TRecord | null>((resolve, reject) => {
      try {
        const tx = db.transaction(this.storeName, "readonly");
        const store = tx.objectStore(this.storeName);
        const request = store.get(id);

        request.onsuccess = () => {
          const result = request.result as TRecord | undefined;
          resolve(result ?? this.memoryStore.get(id) ?? null);
        };
        request.onerror = () => reject(request.error);
      } catch {
        resolve(this.memoryStore.get(id) ?? null);
      }
    });
  }

  async delete(id: string): Promise<boolean> {
    this.memoryStore.delete(id);
    const db = await this.getDB();
    if (!db) {
      return true;
    }

    return new Promise<boolean>((resolve, reject) => {
      try {
        const tx = db.transaction(this.storeName, "readwrite");
        const store = tx.objectStore(this.storeName);
        const request = store.delete(id);

        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      } catch {
        resolve(true);
      }
    });
  }

  async clear(): Promise<void> {
    this.memoryStore.clear();
    const db = await this.getDB();
    if (!db) {
      return;
    }

    return new Promise<void>((resolve, reject) => {
      try {
        const tx = db.transaction(this.storeName, "readwrite");
        const store = tx.objectStore(this.storeName);
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      } catch {
        resolve();
      }
    });
  }

  async getAllKeys(): Promise<string[]> {
    const db = await this.getDB();
    if (!db) {
      return Array.from(this.memoryStore.keys());
    }

    return new Promise<string[]>((resolve, reject) => {
      try {
        const tx = db.transaction(this.storeName, "readonly");
        const store = tx.objectStore(this.storeName);
        const request = store.getAllKeys();

        request.onsuccess = () => {
          const keys = ((request.result as IDBValidKey[]) || []).map(String);
          const combined = new Set([...keys, ...this.memoryStore.keys()]);
          resolve(Array.from(combined));
        };
        request.onerror = () => reject(request.error);
      } catch {
        resolve(Array.from(this.memoryStore.keys()));
      }
    });
  }
}
